<template>
  <span
    v-if="shouldRender"
    data-ds="wangshu"
    class="ws-agent-avatar"
    :class="avatarClasses"
    :style="avatarStyle"
    role="img"
    :aria-label="computedAriaLabel"
  >
    <img
      v-if="resolvedAvatarUrl"
      class="ws-agent-avatar-img avatar-img"
      :src="resolvedAvatarUrl"
      :alt="imageAlt"
      loading="lazy"
      @error="handleImageError"
    >
    <span v-else class="ws-agent-avatar-text avatar-text">{{ displayInitial }}</span>
    <template v-if="!strict">
      <span v-if="autonomyLevel" class="ws-agent-avatar-level">{{ autonomyLevel }}</span>
      <span class="ws-agent-avatar-status" aria-hidden="true" />
      <span
        v-if="decisionPending"
        class="ws-agent-avatar-pending"
        aria-hidden="true"
      />
    </template>
  </span>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  generateAvatarColor,
  getInitial,
  toAvatarSize,
  type AgentAvatarProps,
  type AgentAvatarStatus,
} from '@/utils/agent-avatar'

const props = withDefaults(defineProps<AgentAvatarProps>(), {
  seed: undefined,
  name: undefined,
  label: undefined,
  size: undefined,
  status: 'idle',
  decisionPending: false,
  compact: false,
  shortLabel: undefined,
  avatarUrl: undefined,
  imageAlt: undefined,
  autonomyLevel: undefined,
  ariaLabel: undefined,
  strict: false,
})

const imageFailed = ref(false)

const displayName = computed(() => props.name || props.label || props.seed || 'Agent')
const imageAlt = computed(() => props.imageAlt || displayName.value)
const seed = computed(() => props.seed || props.name || props.label || displayName.value)
const colors = computed(() => generateAvatarColor(seed.value))
const displayInitial = computed(() => getInitial(props.shortLabel || displayName.value))
const sizeValue = computed(() => toAvatarSize(props.size, props.compact ? 28 : 36))
const normalizedStatus = computed<AgentAvatarStatus>(() => props.status ?? 'idle')

const resolvedAvatarUrl = computed(() => {
  if (imageFailed.value || !props.avatarUrl) {
    return ''
  }
  return props.avatarUrl
})

// strict 模式：无真实图片可显示时整组件不渲染，避免色块+首字母兜底假头像。
const shouldRender = computed(() => {
  if (!props.strict) {
    return true
  }
  return Boolean(resolvedAvatarUrl.value)
})

const computedAriaLabel = computed(() => {
  if (props.ariaLabel) {
    return props.ariaLabel
  }
  const pending = props.decisionPending ? '，待决策' : ''
  return `${displayName.value}，${statusText(normalizedStatus.value)}${pending}`
})

const avatarClasses = computed(() => [
  `ws-agent-avatar--${normalizedStatus.value}`,
  {
    'ws-agent-avatar--compact': props.compact,
    'ws-agent-avatar--image': Boolean(resolvedAvatarUrl.value),
    'ws-agent-avatar--pending': props.decisionPending,
  },
])

const avatarStyle = computed<Record<string, string>>(() => ({
  '--ws-agent-avatar-bg': colors.value.bg,
  '--ws-agent-avatar-fg': colors.value.fg,
  width: sizeValue.value,
  height: sizeValue.value,
}))

watch(() => props.avatarUrl, () => {
  imageFailed.value = false
})

function handleImageError(): void {
  imageFailed.value = true
}

function statusText(status: AgentAvatarStatus): string {
  const labels: Record<AgentAvatarStatus, string> = {
    offline: '离线',
    idle: '空闲',
    thinking: '思考中',
    speaking: '输出中',
    working: '工作中',
    awaiting: '等待决策',
    online: '在线',
    busy: '忙碌',
  }
  return labels[status]
}
</script>

<style scoped>
.ws-agent-avatar {
  align-items: center;
  background: var(--ws-agent-avatar-bg);
  border: 1px solid color-mix(in oklch, var(--ws-agent-avatar-bg) 72%, white);
  border-radius: 8px;
  color: var(--ws-agent-avatar-fg);
  display: inline-flex;
  flex: 0 0 auto;
  font-size: calc(min(48px, max(20px, 100%)) * 0.38);
  font-weight: 700;
  justify-content: center;
  line-height: 1;
  overflow: visible;
  position: relative;
  user-select: none;
  vertical-align: middle;
}

.ws-agent-avatar-text {
  letter-spacing: 0;
  position: relative;
  z-index: 1;
}

.ws-agent-avatar-img {
  border-radius: inherit;
  height: 100%;
  inset: 0;
  object-fit: cover;
  position: absolute;
  width: 100%;
  z-index: 1;
}

.ws-agent-avatar-status,
.ws-agent-avatar-pending {
  border: 1.5px solid var(--surface-1);
  border-radius: 50%;
  position: absolute;
  z-index: 2;
}

.ws-agent-avatar-status {
  background: var(--text-faint);
  bottom: -2px;
  height: 9px;
  right: -2px;
  width: 9px;
}

.ws-agent-avatar-pending {
  background: var(--danger);
  height: 8px;
  right: -3px;
  top: -3px;
  width: 8px;
}

.ws-agent-avatar-level {
  align-items: center;
  background: color-mix(in oklch, var(--ws-agent-avatar-fg) 82%, white);
  border-radius: 4px;
  color: white;
  display: inline-flex;
  font-size: 8px;
  font-weight: 700;
  height: 12px;
  justify-content: center;
  left: -4px;
  line-height: 1;
  min-width: 16px;
  padding: 0 3px;
  position: absolute;
  top: -4px;
  z-index: 3;
}

.ws-agent-avatar--offline {
  background: var(--surface-3);
  border-color: var(--hairline);
  color: var(--text-faint);
  opacity: 0.72;
}

.ws-agent-avatar--idle .ws-agent-avatar-status,
.ws-agent-avatar--online .ws-agent-avatar-status {
  background: var(--success);
}

.ws-agent-avatar--working .ws-agent-avatar-status,
.ws-agent-avatar--thinking .ws-agent-avatar-status,
.ws-agent-avatar--busy .ws-agent-avatar-status {
  background: var(--accent);
  animation: ws-agent-avatar-pulse 1.4s ease-in-out infinite;
}

.ws-agent-avatar--awaiting .ws-agent-avatar-status {
  background: var(--danger);
}

.ws-agent-avatar--speaking .ws-agent-avatar-status {
  background: var(--success);
  animation: ws-agent-avatar-blink 0.55s step-end infinite;
}

.ws-agent-avatar--working,
.ws-agent-avatar--thinking,
.ws-agent-avatar--busy {
  box-shadow: 0 0 0 2px color-mix(in oklch, var(--ws-agent-avatar-bg) 30%, transparent);
}

@keyframes ws-agent-avatar-pulse {
  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.25);
  }
}

@keyframes ws-agent-avatar-blink {
  50% {
    opacity: 0.35;
  }
}
</style>
